import React, { useState, useEffect, useMemo } from "react";
import { 
  CreditCard, 
  Download, 
  Search, 
  Filter, 
  ArrowUpRight, 
  ArrowDownLeft, 
  ShieldCheck, 
  CheckCircle2,
  Clock,
  Layers,
  Wallet,
  Building,
  RefreshCw,
  Send,
  AlertCircle,
  TrendingUp,
  Percent,
  Check,
  ChevronRight,
  Sparkles,
  DollarSign
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import { 
  SuperAdminFinanceOverview,
  StaffPayrollRosterItem,
  DynamicServiceBreakdown
} from "../../services/paymentFinanceService";
import { 
  useFinanceSummaryQuery,
  useDisburseCommissionMutation
} from "../../hooks/queries/useJobFinanceQueries";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const SuperAdminPayments: React.FC = () => {
  const currentMonthStr = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthStr);
  const disburseMutation = useDisburseCommissionMutation();

  const {
    data: overview,
    isLoading: loading,
    isFetching: refreshing,
    refetch,
  } = useFinanceSummaryQuery(selectedMonth);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [serviceFilter, setServiceFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");

  // Single Staff Disbursement Modal
  const [disburseTargetStaff, setDisburseTargetStaff] = useState<StaffPayrollRosterItem | null>(null);
  const [disbursePaymentMethod, setDisbursePaymentMethod] = useState<string>("Bank Transfer");
  const [disburseReference, setDisburseReference] = useState<string>("");
  const [disbursing, setDisbursing] = useState<boolean>(false);
  const [disburseSuccessMsg, setDisburseSuccessMsg] = useState<string | null>(null);

  // Batch Month Disbursement Modal
  const [isBatchModalOpen, setIsBatchModalOpen] = useState<boolean>(false);
  const [batchDisbursing, setBatchDisbursing] = useState<boolean>(false);

  const handleRefresh = () => {
    refetch();
  };

  // Filtered staff roster
  const filteredStaffRoster = useMemo(() => {
    if (!overview?.staff_roster) return [];
    return overview.staff_roster.filter((staff) => {
      const matchesSearch = 
        staff.staff_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        staff.staff_email.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesService = 
        serviceFilter === "ALL" || 
        staff.services_handled_codes.includes(serviceFilter);

      let matchesStatus = true;
      if (statusFilter === "PENDING") matchesStatus = Number(staff.pending_payout) > 0;
      if (statusFilter === "PAID") matchesStatus = staff.payout_status === "FULLY_PAID";

      return matchesSearch && matchesService && matchesStatus;
    });
  }, [overview, searchQuery, serviceFilter, statusFilter]);

  // Execute single staff disbursement
  const handleConfirmStaffDisburse = async () => {
    if (!disburseTargetStaff) return;
    try {
      setDisbursing(true);
      const res = await disburseMutation.mutateAsync({
        staff_id: disburseTargetStaff.staff_id,
        month: selectedMonth,
        payment_method: disbursePaymentMethod,
        reference: disburseReference || `PAYOUT-${Date.now().toString().slice(-6)}`,
      });

      setDisburseSuccessMsg(res.detail || "Payout disbursed successfully.");
      setTimeout(() => {
        setDisburseTargetStaff(null);
        setDisburseSuccessMsg(null);
        setDisburseReference("");
      }, 1500);
    } catch (err) {
      console.error("Disbursement failed:", err);
    } finally {
      setDisbursing(false);
    }
  };

  // Execute batch disbursement for all approved staff in month
  const handleConfirmBatchDisburse = async () => {
    try {
      setBatchDisbursing(true);
      const res = await disburseMutation.mutateAsync({
        all_approved: true,
        month: selectedMonth,
        payment_method: "Batch Payroll Settlement",
        reference: `BATCH-${selectedMonth}-${Date.now().toString().slice(-4)}`,
      });

      setDisburseSuccessMsg(res.detail || "Batch payroll disbursed successfully.");
      setTimeout(() => {
        setIsBatchModalOpen(false);
        setDisburseSuccessMsg(null);
      }, 1500);
    } catch (err) {
      console.error("Batch disbursement failed:", err);
    } finally {
      setBatchDisbursing(false);
    }
  };

  const availableMonths = overview?.available_months || [currentMonthStr];
  const summary = overview?.summary;

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
            <span className="cht-px-3 cht-py-1 cht-bg-indigo-50 cht-text-[#3e57da] cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-rounded-xl">
              Executive Treasury
            </span>
            <span className="cht-text-xs cht-font-bold cht-text-gray-400">
              Audit Month: {selectedMonth}
            </span>
          </div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
            SuperAdmin Finance & Payouts
          </h1>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm">
            Master platform treasury, multi-service margin matrix, and specialist commission payroll settlements.
          </p>
        </div>

        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3">
          {/* Month Selector */}
          <div className="cht-w-44">
            <Input
              as="select"
              value={selectedMonth}
              onChange={(e: any) => setSelectedMonth(e.target.value)}
              options={availableMonths.map((m) => ({
                label: new Date(`${m}-01`).toLocaleDateString("en-US", { month: "long", year: "numeric" }),
                value: m,
              }))}
            />
          </div>

          <Button
            variant="outline"
            onClick={handleRefresh}
            leftIcon={<RefreshCw size={16} className={refreshing ? "cht-animate-spin" : ""} />}
            className="cht-rounded-xl"
            title="Refresh treasury data"
          >
            Refresh
          </Button>

          {Number(summary?.total_pending_payout || 0) > 0 && (
            <Button
              variant="primary"
              onClick={() => setIsBatchModalOpen(true)}
              leftIcon={<Sparkles size={16} />}
              className="cht-rounded-xl"
            >
              Disburse Month Payroll
            </Button>
          )}
        </div>
      </header>

      {/* Platform Treasury Deck */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-5">
        {/* Gross Inflow */}
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl">
            <CreditCard size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Gross Revenue (MTD)
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">
              ₦{Number(summary?.gross_revenue || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </h4>
            <span className="cht-text-[11px] cht-font-medium cht-text-emerald-600">
              {summary?.total_deliverables || 0} fulfillments across all services
            </span>
          </div>
        </GlassCard>

        {/* Retained Platform Margin */}
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-blue-50 cht-text-blue-600 cht-rounded-2xl">
            <ArrowUpRight size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Platform Net Retained
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">
              ₦{Number(summary?.platform_margin || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </h4>
            <span className="cht-text-[11px] cht-font-medium cht-text-blue-600">
              Company net surplus for {selectedMonth}
            </span>
          </div>
        </GlassCard>

        {/* Pending Commission Payroll */}
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl">
            <Wallet size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Pending Commission Payroll
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-amber-700">
              ₦{Number(summary?.total_pending_payout || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </h4>
            <span className="cht-text-[11px] cht-font-medium cht-text-amber-600">
              Approved specialist balances
            </span>
          </div>
        </GlassCard>

        {/* Cumulative Settled Payroll */}
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl">
            <ShieldCheck size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Total Disbursed (Lifetime)
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">
              ₦{Number(summary?.lifetime_total_disbursed || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </h4>
            <span className="cht-text-[11px] cht-font-medium cht-text-purple-600">
              Cumulative payroll settled to date
            </span>
          </div>
        </GlassCard>
      </div>

      {/* Dynamic Multi-Service Margin Matrix */}
      <div className="cht-space-y-3">
        <div className="cht-flex cht-items-center cht-justify-between">
          <div className="cht-flex cht-items-center cht-gap-2">
            <Layers size={18} className="cht-text-[#3e57da]" />
            <h3 className="cht-text-base cht-font-black cht-text-gray-900">
              Dynamic Multi-Service Margin Matrix ({selectedMonth})
            </h3>
          </div>
          <span className="cht-text-xs cht-font-bold cht-text-gray-400">
            {overview?.service_breakdown?.length || 0} Registered Service Offerings
          </span>
        </div>

        <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-4">
          {overview?.service_breakdown && overview.service_breakdown.length > 0 ? (
            overview.service_breakdown.map((sb: DynamicServiceBreakdown) => (
              <GlassCard
                key={sb.service_code}
                className="cht-p-5 cht-rounded-[24px] cht-border-l-4 cht-border-l-[#3e57da] hover:cht-shadow-md cht-transition-all"
              >
                <div className="cht-flex cht-items-start cht-justify-between cht-gap-2">
                  <div>
                    <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                      {sb.service_code}
                    </span>
                    <h5 className="cht-text-sm cht-font-black cht-text-gray-800 cht-truncate cht-max-w-[170px]">
                      {sb.service_name}
                    </h5>
                  </div>
                  <span className="cht-px-2.5 cht-py-1 cht-bg-emerald-50 cht-text-emerald-700 cht-text-[10px] cht-font-black cht-rounded-lg">
                    {sb.margin_percent || 0}% margin
                  </span>
                </div>

                <div className="cht-mt-4 cht-space-y-1.5 cht-text-xs cht-pt-3 cht-border-t cht-border-gray-50">
                  <div className="cht-flex cht-justify-between">
                    <span className="cht-text-gray-400 cht-font-medium">Gross Inflow:</span>
                    <span className="cht-font-black cht-text-gray-900">
                      ₦{Number(sb.gross_revenue || 0).toLocaleString("en-NG")}
                    </span>
                  </div>
                  <div className="cht-flex cht-justify-between">
                    <span className="cht-text-gray-400 cht-font-medium">Direct Portal Cost:</span>
                    <span className="cht-font-bold cht-text-rose-600">
                      -₦{Number(sb.direct_cost || 0).toLocaleString("en-NG")}
                    </span>
                  </div>
                  <div className="cht-flex cht-justify-between">
                    <span className="cht-text-gray-400 cht-font-medium">Specialist Cut:</span>
                    <span className="cht-font-bold cht-text-indigo-600">
                      -₦{Number(sb.total_commission || 0).toLocaleString("en-NG")}
                    </span>
                  </div>
                  <div className="cht-flex cht-justify-between cht-pt-1.5 cht-border-t cht-border-gray-100">
                    <span className="cht-text-gray-700 cht-font-black">Net Profit:</span>
                    <span className="cht-font-black cht-text-emerald-600">
                      ₦{Number(sb.profit || 0).toLocaleString("en-NG")}
                    </span>
                  </div>
                </div>
              </GlassCard>
            ))
          ) : (
            <div className="cht-col-span-4">
              <GlassCard className="cht-p-8 cht-rounded-[24px] cht-text-center cht-text-gray-400">
                <p className="cht-text-sm cht-font-medium">No service deliverables logged for {selectedMonth}.</p>
              </GlassCard>
            </div>
          )}
        </div>
      </div>

      {/* Monthly Staff Payroll Settlement Center */}
      <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
        <div className="cht-p-5 cht-border-b cht-border-gray-100 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-4">
          <div>
            <h4 className="cht-text-base cht-font-black cht-text-gray-900">
              Staff Commission Payroll Roster ({selectedMonth})
            </h4>
            <p className="cht-text-xs cht-font-medium cht-text-gray-400">
              Review and disburse monthly commission earnings to specialists across all active services.
            </p>
          </div>

          <div className="cht-flex cht-items-center cht-gap-3">
            <div className="cht-w-44">
              <Input
                as="select"
                value={serviceFilter}
                onChange={(e: any) => setServiceFilter(e.target.value)}
                options={[
                  { label: "All Services", value: "ALL" },
                  ...(overview?.service_breakdown || []).map((sb) => ({
                    label: sb.service_name,
                    value: sb.service_code,
                  })),
                ]}
              />
            </div>
            <div className="cht-w-40">
              <Input
                as="select"
                value={statusFilter}
                onChange={(e: any) => setStatusFilter(e.target.value)}
                options={[
                  { label: "All Statuses", value: "ALL" },
                  { label: "Pending Payout", value: "PENDING" },
                  { label: "Fully Settled", value: "PAID" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="cht-p-4 cht-border-b cht-border-gray-100/60 cht-bg-gray-50/20">
          <Input
            leftIcon={<Search size={18} />}
            placeholder="Search staff by name or email..."
            value={searchQuery}
            onChange={(e: any) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="cht-overflow-x-auto">
          <table className="cht-w-full cht-text-left cht-border-collapse">
            <thead>
              <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/40 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                <th className="cht-py-4 cht-px-6">Specialist</th>
                <th className="cht-py-4 cht-px-6">Deliverables</th>
                <th className="cht-py-4 cht-px-6">Services Handled</th>
                <th className="cht-py-4 cht-px-6">Accrued Cut</th>
                <th className="cht-py-4 cht-px-6">Settled</th>
                <th className="cht-py-4 cht-px-6">Pending Payout</th>
                <th className="cht-py-4 cht-px-6">Status</th>
                <th className="cht-py-4 cht-px-6 cht-text-right">Action</th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-50">
              {filteredStaffRoster.length > 0 ? (
                filteredStaffRoster.map((staff: StaffPayrollRosterItem) => (
                  <tr key={staff.staff_id} className="hover:cht-bg-gray-50/50 cht-transition-colors">
                    <td className="cht-py-4 cht-px-6">
                      <h5 className="cht-text-xs cht-font-black cht-text-gray-900">{staff.staff_name}</h5>
                      <span className="cht-text-[11px] cht-text-gray-400 cht-font-medium">{staff.staff_email}</span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <span className="cht-text-xs cht-font-bold cht-text-gray-700">
                        {staff.approved_deliverables_count} / {staff.total_deliverables} approved
                      </span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <div className="cht-flex cht-flex-wrap cht-gap-1.5 max-w-[240px]">
                        {staff.services_handled.map((sName, idx) => (
                          <span
                            key={idx}
                            className="cht-px-2 cht-py-0.5 cht-bg-blue-50 cht-text-[#3e57da] cht-text-[10px] cht-font-black cht-rounded-md"
                          >
                            {sName}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <span className="cht-text-xs cht-font-bold cht-text-gray-900">
                        ₦{Number(staff.total_earned || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                      </span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <span className="cht-text-xs cht-font-bold cht-text-emerald-600">
                        ₦{Number(staff.total_paid || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                      </span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <span className={`cht-text-xs cht-font-black ${
                        Number(staff.pending_payout) > 0 ? "cht-text-indigo-600" : "cht-text-gray-400"
                      }`}>
                        ₦{Number(staff.pending_payout || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                      </span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      {Number(staff.pending_payout) > 0 ? (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-amber-50 cht-text-amber-700 cht-text-[10px] cht-font-black">
                          <Clock size={11} />
                          Pending Payout
                        </span>
                      ) : Number(staff.total_earned) > 0 ? (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-emerald-50 cht-text-emerald-700 cht-text-[10px] cht-font-black">
                          <CheckCircle2 size={11} />
                          Fully Settled
                        </span>
                      ) : (
                        <span className="cht-text-xs cht-text-gray-400 cht-font-medium">No Commission</span>
                      )}
                    </td>

                    <td className="cht-py-4 cht-px-6 cht-text-right">
                      {Number(staff.pending_payout) > 0 ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setDisburseTargetStaff(staff)}
                          className="cht-rounded-lg cht-text-[11px] cht-py-1.5 cht-px-3.5"
                          leftIcon={<Send size={12} />}
                        >
                          Disburse
                        </Button>
                      ) : (
                        <span className="cht-text-xs cht-font-bold cht-text-emerald-600 cht-inline-flex cht-items-center cht-gap-1">
                          <Check size={14} /> Settled
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="cht-py-12 cht-text-center cht-text-gray-400">
                    <p className="cht-text-sm cht-font-medium">No specialist payroll records match filter criteria.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Single Staff Disbursement Modal */}
      {disburseTargetStaff && (
        <Modal
          isOpen={Boolean(disburseTargetStaff)}
          onClose={() => !disbursing && setDisburseTargetStaff(null)}
          title={`Disburse Payout to ${disburseTargetStaff.staff_name}`}
        >
          <div className="cht-space-y-4 cht-p-2">
            {disburseSuccessMsg ? (
              <div className="cht-p-4 cht-bg-emerald-50 cht-border cht-border-emerald-200 cht-text-emerald-800 cht-rounded-2xl cht-text-center cht-space-y-2">
                <CheckCircle2 size={32} className="cht-mx-auto cht-text-emerald-600" />
                <h4 className="cht-font-black cht-text-base">Payout Recorded Successfully!</h4>
                <p className="cht-text-xs">{disburseSuccessMsg}</p>
              </div>
            ) : (
              <>
                <div className="cht-bg-gray-50 cht-p-4 cht-rounded-2xl cht-space-y-2">
                  <div className="cht-flex cht-justify-between cht-text-xs">
                    <span className="cht-text-gray-400 cht-font-bold">Specialist</span>
                    <span className="cht-font-black cht-text-gray-900">{disburseTargetStaff.staff_name}</span>
                  </div>
                  <div className="cht-flex cht-justify-between cht-text-xs">
                    <span className="cht-text-gray-400 cht-font-bold">Payroll Month</span>
                    <span className="cht-font-bold cht-text-gray-700">{selectedMonth}</span>
                  </div>
                  <div className="cht-flex cht-justify-between cht-text-xs">
                    <span className="cht-text-gray-400 cht-font-bold">Approved Deliverables</span>
                    <span className="cht-font-bold cht-text-gray-700">{disburseTargetStaff.approved_deliverables_count} items</span>
                  </div>
                  <div className="cht-flex cht-justify-between cht-pt-2 cht-border-t cht-border-gray-200">
                    <span className="cht-text-xs cht-font-black cht-text-gray-900">Total Payable Amount</span>
                    <span className="cht-text-base cht-font-black cht-text-[#3e57da]">
                      ₦{Number(disburseTargetStaff.pending_payout).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <div className="cht-space-y-3">
                  <div>
                    <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-600 cht-mb-1">
                      Disbursement Method
                    </label>
                    <Input
                      as="select"
                      value={disbursePaymentMethod}
                      onChange={(e: any) => setDisbursePaymentMethod(e.target.value)}
                      options={[
                        { label: "Bank Transfer (Direct Settlement)", value: "Bank Transfer" },
                        { label: "Paystack Automated Settlement", value: "Paystack Settlement" },
                        { label: "Corporate Wallet Credit", value: "Wallet Transfer" },
                      ]}
                    />
                  </div>

                  <div>
                    <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-600 cht-mb-1">
                      Payment Reference / Bank Transaction ID
                    </label>
                    <Input
                      placeholder="e.g. TRF-202609-00124"
                      value={disburseReference}
                      onChange={(e: any) => setDisburseReference(e.target.value)}
                    />
                  </div>
                </div>

                <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-3">
                  <Button
                    variant="outline"
                    onClick={() => setDisburseTargetStaff(null)}
                    disabled={disbursing}
                    className="cht-rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleConfirmStaffDisburse}
                    loading={disbursing}
                    leftIcon={<Send size={14} />}
                    className="cht-rounded-xl"
                  >
                    Authorize Payout
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Batch Month Disbursement Modal */}
      {isBatchModalOpen && (
        <Modal
          isOpen={isBatchModalOpen}
          onClose={() => !batchDisbursing && setIsBatchModalOpen(false)}
          title={`Disburse All Approved Payouts for ${selectedMonth}`}
        >
          <div className="cht-space-y-4 cht-p-2">
            {disburseSuccessMsg ? (
              <div className="cht-p-4 cht-bg-emerald-50 cht-border cht-border-emerald-200 cht-text-emerald-800 cht-rounded-2xl cht-text-center cht-space-y-2">
                <CheckCircle2 size={32} className="cht-mx-auto cht-text-emerald-600" />
                <h4 className="cht-font-black cht-text-base">Batch Payroll Disbursed!</h4>
                <p className="cht-text-xs">{disburseSuccessMsg}</p>
              </div>
            ) : (
              <>
                <div className="cht-p-4 cht-bg-indigo-50/60 cht-border cht-border-indigo-100 cht-rounded-2xl cht-space-y-2">
                  <p className="cht-text-xs cht-text-indigo-800 cht-font-medium">
                    You are about to authorize the complete monthly commission payroll for all approved specialists for <strong>{selectedMonth}</strong>.
                  </p>
                  <div className="cht-flex cht-justify-between cht-text-xs cht-pt-2 cht-border-t cht-border-indigo-200">
                    <span className="cht-font-bold cht-text-indigo-900">Total Pending Payroll</span>
                    <span className="cht-text-base cht-font-black cht-text-[#3e57da]">
                      ₦{Number(summary?.total_pending_payout || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsBatchModalOpen(false)}
                    disabled={batchDisbursing}
                    className="cht-rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleConfirmBatchDisburse}
                    loading={batchDisbursing}
                    leftIcon={<Sparkles size={14} />}
                    className="cht-rounded-xl"
                  >
                    Authorize Batch Payroll
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SuperAdminPayments;

